import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FiPhone } from "react-icons/fi";
import "./BoardsDetailsSpecifications.css";
import {
  matchesNotesSectionHeading,
  resolveSpecificationRowParts,
} from "../../../../../utils/specificationTable";
import { formatUnitSuperscriptText } from "../../../../../utils/unitSuperscriptText";
import checkersAllImg from "../../../../../assets/images/allproducts/boards/checkersall.png";
import regularGallery1 from "../../../../../assets/images/allproducts/boards/regularGallery/regular pallete.png";
import regularGallery2 from "../../../../../assets/images/allproducts/boards/regularGallery/regular.png";
import regularGallery3 from "../../../../../assets/images/allproducts/boards/regularGallery/IRQGYP REGULAR board.png";
import moustureGallery1 from "../../../../../assets/images/allproducts/boards/moustureGallery/moisture pallete .png";
import moustureGallery2 from "../../../../../assets/images/allproducts/boards/moustureGallery/TDS gypsum aquaguard.png";
import moustureGallery3 from "../../../../../assets/images/allproducts/boards/moustureGallery/Moisture.png";
import moistureGallery1 from "../../../../../assets/images/allproducts/boards/moistureGallery/moisture pallete .png";
import moistureGallery2 from "../../../../../assets/images/allproducts/boards/moistureGallery/TDS gypsum al ahli h3.png";
import moistureGallery3 from "../../../../../assets/images/allproducts/boards/moistureGallery/Moisture.png";
import fireGallery1 from "../../../../../assets/images/allproducts/boards/fireGallery/fire pallete.png";
import fireGallery2 from "../../../../../assets/images/allproducts/boards/fireGallery/IRQGYP fire resistance.png";
import fireGallery3 from "../../../../../assets/images/allproducts/boards/fireGallery/pink.png";
import fmrGallery1 from "../../../../../assets/images/allproducts/boards/fmrGallery/FMR pallete.png";
import fmrGallery2 from "../../../../../assets/images/allproducts/boards/fmrGallery/IRQGYP FMR board.png";
import fmrGallery3 from "../../../../../assets/images/allproducts/boards/fmrGallery/FMR.png";
import exteriorGallery1 from "../../../../../assets/images/allproducts/boards/exteriorGallery/exterior pallete.png";
import exteriorGallery2 from "../../../../../assets/images/allproducts/boards/exteriorGallery/exterior.png";
import exteriorGallery3 from "../../../../../assets/images/allproducts/boards/exteriorGallery/sheildex.board png.png";

const SALES_PHONE_TEL = "+9647502020066";

const DEFAULT_SPEC_DESCRIPTION =
  "Regular gypsum board is a factory-manufactured, paper-faced gypsum plasterboard designed for general internal wall and ceiling linings in dry, conditioned environments.";

const DEFAULT_SPEC_SECTIONS = [
  {
    heading: "Physical Properties",
    items: [
      { label: "Thickness - Nominal", value: "9 - 12.5 - 15 - 15.9 mm" },
      { label: "Width - Nominal", value: "1,200 mm" },
      { label: "Length - Standard", value: "2,400 mm - 3,000 mm" },
      { label: "Weight - Nominal", value: "6.19kg/m2" },
      { label: "Edges", value: "TE, SE" },
      { label: "Colour", value: "Gray Colour" },
    ],
  },
  {
    heading: "Fire-Resistance Characteristics ASTM E84",
    items: [
      { label: "Core type", value: "Type C" },
      { label: "Width - Nominal", value: "1,200 mm" },
      { label: "Length - Standard", value: "2,400 mm, 3,000 mm" },
      { label: "Weight - Nominal", value: "6.192kg/m2" },
    ],
  },
  {
    heading: "Standards and References",
    items: [
      { label: "ASTM C1396", value: "Product standard compliance" },
      {
        label: "ASTM C473",
        value: "Standard test methods for physical testing of gypsum panel products",
      },
      {
        label: "ASTM E84",
        value: "Standard test method for surface burning characteristics of building materials",
      },
      { label: "EN 520", value: "Type A, performance of gypsum plasterboards" },
    ],
  },
  {
    heading: "Service Life",
    items: [
      {
        label: "Declared service life",
        value:
          "Declared service life is 60 years and/or the design life of the building, provided boards are stored, installed, and maintained per this datasheet and relevant standards.",
      },
    ],
  },
];

const BOARD_GALLERY_IMAGES = {
  regular: [regularGallery1, regularGallery2, regularGallery3],
  mousture: [moustureGallery1, moustureGallery2, moustureGallery3],
  moisture: [moistureGallery1, moistureGallery2, moistureGallery3],
  fire: [fireGallery1, fireGallery2, fireGallery3],
  fmr: [fmrGallery1, fmrGallery2, fmrGallery3],
  exterior: [exteriorGallery1, exteriorGallery2, exteriorGallery3],
};

const STANDARD_CODE_REGEX = /\b(?:ASTM|EN)\s*[A-Z]?\d+(?:-\d+)?\b/gi;

const getStandardCodesFromText = (text) => {
  if (!text) return [];
  const matches = String(text).match(STANDARD_CODE_REGEX);
  return matches ? matches.map((code) => code.trim()) : [];
};

const uniqueCodes = (codes) => [...new Set(codes.filter(Boolean))];

const removeStandardCodesFromText = (text) => {
  if (!text) return "";
  return String(text)
    .replace(STANDARD_CODE_REGEX, "")
    .replace(/\s{2,}/g, " ")
    .replace(/\s*[,/;-]\s*$/, "")
    .trim();
};

const formatSpecText = (text) => formatUnitSuperscriptText(text || "");

const BoardsDetailsSpecifications = ({ board }) => {
  const sliderRef = useRef(null);
  const dragStateRef = useRef({ isDown: false, startX: 0, scrollLeft: 0 });
  const [isDragging, setIsDragging] = useState(false);

  const description =
    board?.specificationsDescription ??
    board?.heroDescription ??
    DEFAULT_SPEC_DESCRIPTION;

  const sections =
    Array.isArray(board?.specificationSections) && board.specificationSections.length
      ? board.specificationSections
      : DEFAULT_SPEC_SECTIONS;
  const standardsSection = sections.find((section) =>
    /standards?/i.test(section?.heading || "")
  );
  const globalStandardCodes = uniqueCodes([
    ...getStandardCodesFromText(standardsSection?.heading),
    ...(Array.isArray(standardsSection?.items)
      ? standardsSection.items.flatMap((item) => [
          ...getStandardCodesFromText(item?.label),
          ...getStandardCodesFromText(item?.value),
        ])
      : []),
  ]);
  const boardId = String(board?.id || "").toLowerCase().trim();
  const miniSliderImages =
    BOARD_GALLERY_IMAGES[boardId]?.filter(Boolean) || [checkersAllImg];

  const onMouseDown = (event) => {
    const slider = sliderRef.current;
    if (!slider) return;
    dragStateRef.current = {
      isDown: true,
      startX: event.pageX - slider.offsetLeft,
      scrollLeft: slider.scrollLeft,
    };
    setIsDragging(true);
  };

  const onMouseMove = (event) => {
    const slider = sliderRef.current;
    const drag = dragStateRef.current;
    if (!slider || !drag.isDown) return;
    event.preventDefault();
    const x = event.pageX - slider.offsetLeft;
    const walk = (x - drag.startX) * 1.2;
    slider.scrollLeft = drag.scrollLeft - walk;
  };

  const stopDragging = () => {
    dragStateRef.current.isDown = false;
    setIsDragging(false);
  };

  return (
    <section className="boardsSpec" aria-label="Specifications">
      <div className="boardsSpec-inner">
        <div className="boardsSpec-left">
          <div className="boardsSpec-checkers" aria-label="Board preview images">
            <div
              ref={sliderRef}
              className={`boardsSpec-miniSlider ${isDragging ? "is-grabbing" : ""}`}
              onMouseDown={onMouseDown}
              onMouseMove={onMouseMove}
              onMouseLeave={stopDragging}
              onMouseUp={stopDragging}
            >
              {miniSliderImages.map((image, index) => (
                <div className="boardsSpec-miniSlide" key={`mini-slide-${index}`}>
                  <img
                    src={image}
                    alt={`Board preview ${index + 1}`}
                    className="boardsSpec-checkersImg"
                    draggable={false}
                  />
                </div>
              ))}
            </div>
          </div>

          <h2 className="boardsSpec-descHeading">Description</h2>
          <p className="boardsSpec-descText">{description}</p>

          <div className="boardsSpec-actions">
            <Link to="/contactus#contact-form" className="boardsSpec-btnQuote">
              Get a quote
            </Link>
            <a href={`tel:${SALES_PHONE_TEL}`} className="boardsSpec-btnCall">
              <span>Or Call us now</span>
              <FiPhone className="boardsSpec-phoneIcon" aria-hidden />
            </a>
          </div>
        </div>

        <div className="boardsSpec-right">
          <h2 className="boardsSpec-mainHeading">SPECIFICATIONS</h2>

          {sections.map((section, sectionIndex) => {
            const items = Array.isArray(section?.items) ? section.items : [];
            const heading = section?.heading || "";
            const isServiceLife = /service life/i.test(heading);
            const isStandardsSection = /standards?/i.test(heading);
            const isNotesSection = matchesNotesSectionHeading(heading);
            const sectionStandardCodes = uniqueCodes([
              ...getStandardCodesFromText(heading),
              ...items.flatMap((item) => [
                ...getStandardCodesFromText(item?.label),
                ...getStandardCodesFromText(item?.value),
              ]),
            ]);
            const inferredSectionStandard =
              sectionStandardCodes[0] || globalStandardCodes[0] || "";
            const hasReferenceColumn = !isServiceLife && !isStandardsSection && !isNotesSection;

            return (
              <section className="boardsSpec-block" key={`${heading}-${sectionIndex}`}>
                <h3 className="boardsSpec-subHeading">{heading}</h3>
                <div className="boardsSpec-rule" />
                {isServiceLife && items[0]?.value ? (
                  <p className="boardsSpec-serviceText">{formatSpecText(items[0].value)}</p>
                ) : isNotesSection ? (
                  <div className="boardsSpec-notes">
                    {items.map((item, noteIndex) => {
                      const titleRaw = String(item?.label ?? "").trim();
                      const valueRaw = String(item?.value ?? "").trim();
                      const body =
                        removeStandardCodesFromText(valueRaw) || valueRaw || "";
                      if (!titleRaw && !body) return null;
                      return (
                        <div
                          className="boardsSpec-noteBlock"
                          key={`${titleRaw || "note"}-${noteIndex}`}
                        >
                          {titleRaw ? (
                            <h4 className="boardsSpec-noteHeading">{formatSpecText(titleRaw)}</h4>
                          ) : null}
                          {body ? (
                            <p className="boardsSpec-noteText">{formatSpecText(body)}</p>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="boardsSpec-tableWrap">
                    <table
                      className={`boardsSpec-table ${
                        isStandardsSection ? "boardsSpec-table--standards" : ""
                      }`}
                    >
                      <thead>
                        <tr>
                          <th scope="col">Specifications</th>
                          <th scope="col">Units</th>
                          <th scope="col">Value</th>
                          <th scope="col">Standard</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item, itemIndex) => {
                          let displayValue = "";
                          let unitCell = "—";
                          let specificationText = item?.label || "";
                          if (isStandardsSection) {
                            displayValue = item?.value || "";
                          } else {
                            const parts = resolveSpecificationRowParts(item);
                            specificationText = parts.specificationText;
                            unitCell = parts.unit ? formatSpecText(parts.unit) : "—";
                            displayValue =
                              removeStandardCodesFromText(parts.valueText) ||
                              parts.valueText ||
                              "";
                          }
                          const standardCell = hasReferenceColumn
                            ? item?.reference ||
                              item?.standard ||
                              item?.astm ||
                              getStandardCodesFromText(item?.value)[0] ||
                              getStandardCodesFromText(item?.label)[0] ||
                              inferredSectionStandard ||
                              "—"
                            : item?.reference ||
                              item?.standard ||
                              item?.label ||
                              getStandardCodesFromText(item?.value)[0] ||
                              getStandardCodesFromText(item?.label)[0] ||
                              "—";

                          return (
                            <tr key={`${item?.label || "item"}-${itemIndex}`}>
                              <td className="boardsSpec-tdSpec">{formatSpecText(specificationText)}</td>
                              <td className="boardsSpec-tdUnit">{unitCell}</td>
                              <td className="boardsSpec-tdValue">{formatSpecText(displayValue)}</td>
                              <td className="boardsSpec-tdStd">{formatSpecText(standardCell)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default BoardsDetailsSpecifications;
